import React, { useState } from 'react';
import { FaWallet, FaBolt, FaGift, FaSyncAlt, FaInfoCircle, FaHistory, FaShareSquare } from 'react-icons/fa';
import { Button, Tooltip } from 'antd';
import ReversalHistoryModal from './ReversalHistoryModal';
import ReversalAssignModal from './ReversalAssignModal';

function ReversalWalletCards({ walletData, loading, onRefresh }) {
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedCardForHistory, setSelectedCardForHistory] = useState(null);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedCardForAssign, setSelectedCardForAssign] = useState(null);

  const handleOpenHistory = (card) => {
    setSelectedCardForHistory(card);
    setHistoryModalOpen(true);
  };

  const handleOpenAssign = (card) => {
    setSelectedCardForAssign(card);
    setAssignModalOpen(true);
  };

  const formatCurrency = (val) => {
    return Number(val || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const cards = [
    {
      id: 'main',
      title: 'Main Reversal Pool',
      walletKey: 'reversal_main_wallet',
      queryWalletType: 'Main Wallet',
      balance: walletData?.reversal_main_wallet ?? 0,
      icon: <FaWallet size={22} />,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      lightBg: 'rgba(16, 185, 129, 0.08)',
      borderColor: 'rgba(16, 185, 129, 0.25)',
      badgeBg: '#d1fae5',
      badgeColor: '#065f46',
      badgeText: 'All Users (Retailer/Dist/SD)',
      description: 'Funds reversed from Retailer/Distributor Main Wallets'
    },
    {
      id: 'aeps',
      title: 'AEPS Reversal Pool',
      walletKey: 'reversal_aeps_wallet',
      queryWalletType: 'Aeps Wallet',
      balance: walletData?.reversal_aeps_wallet ?? 0,
      icon: <FaBolt size={22} />,
      gradient: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
      lightBg: 'rgba(2, 132, 199, 0.08)',
      borderColor: 'rgba(2, 132, 199, 0.25)',
      badgeBg: '#e0f2fe',
      badgeColor: '#075985',
      badgeText: 'Retailers Only',
      description: 'Funds reversed from Retailer AEPS Wallets'
    },
    {
      id: 'cashback',
      title: 'Cashback Reversal Pool',
      walletKey: 'reversal_cashback_wallet',
      queryWalletType: 'Cashback Wallet',
      balance: walletData?.reversal_cashback_wallet ?? 0,
      icon: <FaGift size={22} />,
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
      lightBg: 'rgba(139, 92, 246, 0.08)',
      borderColor: 'rgba(139, 92, 246, 0.25)',
      badgeBg: '#ede9fe',
      badgeColor: '#5b21b6',
      badgeText: 'Retailers Only',
      description: 'Funds reversed from Retailer Cashback Wallets'
    }
  ];

  return (
    <div className="row m-4 mb-2">
      <div className="col-12">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px'
          }}
        >
          <div>
            <h4
              style={{
                margin: 0,
                fontWeight: 700,
                color: '#1e293b',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>💼 Reversal Holding Pools</span>
              <Tooltip title="When Admin reverses money from any retailer or distributor wallet, it is held in these Admin pools until rotated or assigned back.">
                <FaInfoCircle size={15} color="#94a3b8" style={{ cursor: 'pointer' }} />
              </Tooltip>
            </h4>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.875rem' }}>
              Active Admin float balances held from reversals (click History on any pool to see the exact breakdown)
            </p>
          </div>

          <div>
            <Button
              type="default"
              icon={<FaSyncAlt className={loading ? 'fa-spin' : ''} style={{ marginRight: 6 }} />}
              onClick={onRefresh}
              loading={loading}
              style={{
                borderRadius: '8px',
                borderColor: '#cbd5e1',
                color: '#334155',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center'
              }}
            >
              Refresh Balances
            </Button>
          </div>
        </div>

        {/* Equal height cards container */}
        <div className="row g-3 align-items-stretch">
          {cards.map((card) => (
            <div key={card.id} className="col-xl-4 col-md-6 col-12 d-flex">
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '14px',
                  border: `1px solid ${card.borderColor}`,
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
                  padding: '20px 22px',
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  height: '100%',
                  minHeight: '215px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
              >
                {/* Top decorative gradient bar */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: card.gradient
                  }}
                />

                {/* Top content wrapper */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Badge container with fixed min-height for vertical alignment */}
                    <div style={{ minHeight: '28px', display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <span
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          padding: '3px 10px',
                          borderRadius: '20px',
                          background: card.badgeBg,
                          color: card.badgeColor,
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                          whiteSpace: 'nowrap',
                          display: 'inline-block'
                        }}
                      >
                        {card.badgeText}
                      </span>
                    </div>

                    <h6
                      style={{
                        margin: '0 0 6px',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#475569',
                        minHeight: '22px'
                      }}
                    >
                      {card.title}
                    </h6>

                    <div
                      style={{
                        fontSize: '1.65rem',
                        fontWeight: 800,
                        color: '#0f172a',
                        letterSpacing: '-0.5px',
                        lineHeight: 1.2,
                        minHeight: '36px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      ₹{formatCurrency(card.balance)}
                    </div>
                  </div>

                  <div
                    style={{
                      width: '46px',
                      height: '46px',
                      borderRadius: '12px',
                      background: card.gradient,
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
                      flexShrink: 0
                    }}
                  >
                    {card.icon}
                  </div>
                </div>

                {/* Bottom description wrapper with History button */}
                <div
                  style={{
                    marginTop: '16px',
                    paddingTop: '12px',
                    borderTop: '1px dashed #e2e8f0',
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.35, flex: 1 }}>
                    {card.description}
                  </span>

                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                    <Button
                      size="small"
                      type="default"
                      icon={<FaHistory size={11} style={{ marginRight: 4 }} />}
                      onClick={() => handleOpenHistory(card)}
                      style={{
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        height: '28px',
                        padding: '0 8px',
                        borderColor: card.borderColor,
                        color: card.badgeColor,
                        background: card.lightBg,
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      History
                    </Button>

                    <Button
                      size="small"
                      type="primary"
                      icon={<FaShareSquare size={11} style={{ marginRight: 4 }} />}
                      onClick={() => handleOpenAssign(card)}
                      disabled={Number(card.balance || 0) <= 0}
                      style={{
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        height: '28px',
                        padding: '0 8px',
                        background: Number(card.balance || 0) > 0 ? card.gradient : undefined,
                        borderColor: 'transparent',
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: Number(card.balance || 0) > 0 ? 'pointer' : 'not-allowed'
                      }}
                    >
                      Assign
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* History Breakdown Modal */}
        {selectedCardForHistory && (
          <ReversalHistoryModal
            open={historyModalOpen}
            onClose={() => setHistoryModalOpen(false)}
            walletType={selectedCardForHistory.queryWalletType}
            walletTitle={selectedCardForHistory.title}
            totalBalance={selectedCardForHistory.balance}
            gradient={selectedCardForHistory.gradient}
            badgeBg={selectedCardForHistory.badgeBg}
            badgeColor={selectedCardForHistory.badgeColor}
            icon={selectedCardForHistory.icon}
          />
        )}

        {/* Assign Balance Modal */}
        {selectedCardForAssign && (
          <ReversalAssignModal
            open={assignModalOpen}
            onClose={() => setAssignModalOpen(false)}
            poolType={selectedCardForAssign.queryWalletType}
            poolTitle={selectedCardForAssign.title}
            availableBalance={selectedCardForAssign.balance}
            gradient={selectedCardForAssign.gradient}
            icon={selectedCardForAssign.icon}
            badgeBg={selectedCardForAssign.badgeBg}
            badgeColor={selectedCardForAssign.badgeColor}
            onSuccess={onRefresh}
          />
        )}
      </div>
    </div>
  );
}

export default ReversalWalletCards;
